import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { LayoutDashboard, Users, Activity, DollarSign, BarChart3, Loader2, Shield, AlertCircle, Bot } from "lucide-react";

import AdminDashboard from "./admin/AdminDashboard";
import AdminAnalytics from "./admin/AdminAnalytics";
import AdminUsage from "./admin/AdminUsage";
import AdminCosts from "./admin/AdminCosts";
import AdminUsers from "./admin/AdminUsers";
import AdminAIUsage from "./admin/AdminAIUsage";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "usage", label: "Usage Stats", icon: Activity },
  { id: "ai-usage", label: "AI Alerts", icon: Bot },
  { id: "costs", label: "Costs", icon: DollarSign },
  { id: "users", label: "Users", icon: Users },
];

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [period, setPeriod] = useState(30);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);

  useEffect(() => { checkAdmin(); }, []);
  useEffect(() => { if (isAdmin) loadTab(activeTab); }, [activeTab, isAdmin, period]);

  const checkAdmin = async () => {
    try {
      const r = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(r.data.is_admin);
      if (!r.data.is_admin) { toast.error("Admin access required"); navigate("/dashboard"); }
    } catch { setIsAdmin(false); navigate("/dashboard"); }
  };

  const loadTab = async (tab) => {
    setLoading(true);
    try {
      if (tab === "dashboard") {
        const r = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
        setData((p) => ({ ...p, dashboard: r.data }));
      } else if (tab === "analytics" || tab === "usage") {
        const r = await axios.get(`${API}/admin/analytics`, { params: { period }, withCredentials: true });
        setData((p) => ({ ...p, analytics: r.data }));
      } else if (tab === "costs") {
        const r = await axios.get(`${API}/admin/costs`, { withCredentials: true });
        setData((p) => ({ ...p, costs: r.data }));
      } else if (tab === "users") {
        await loadUsers();
      }
    } catch { toast.error("Failed to load data"); } finally { setLoading(false); }
  };

  const loadUsers = async (search = searchQuery) => {
    const r = await axios.get(`${API}/admin/users`, { params: { search: search || undefined }, withCredentials: true });
    setUsers(r.data.users); setUsersTotal(r.data.total);
  };

  const handleExportCSV = async () => {
    try {
      const r = await axios.get(`${API}/admin/users/export-csv`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "users_export.csv"; a.click();
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const handleUpdateRole = async (userId, role) => {
    await axios.put(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
    toast.success(`Role → ${role}`); loadUsers(); setShowUserDialog(false);
  };

  const handleToggleSub = async (userId, sub) => {
    await axios.put(`${API}/admin/users/${userId}/subscription`, { subscription: sub }, { withCredentials: true });
    toast.success(`Subscription → ${sub}`); loadUsers(); setShowUserDialog(false);
  };

  const handleExtendTrial = async (userId) => {
    const r = await axios.put(`${API}/admin/users/${userId}/extend-trial`, {}, { withCredentials: true });
    toast.success(r.data.message); loadUsers();
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user and ALL their data? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
    toast.success("User deleted"); loadUsers(); setShowUserDialog(false);
  };

  if (isAdmin === null) return <div className="page-container flex items-center justify-center"><Loader2 className="w-8 h-8 text-violet-400 animate-spin" /></div>;
  if (!isAdmin) return <div className="page-container flex flex-col items-center justify-center p-4"><AlertCircle className="w-16 h-16 text-red-400 mb-4" /><h1 className="text-2xl font-bold text-white">Access Denied</h1></div>;

  return (
    <div className="page-container pb-28" data-testid="admin-page">
      {/* Header */}
      <div className="bg-slate-900/60 border-b border-white/8 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-xs text-slate-500">Manage Explorio</p>
            </div>
          </div>
          <Button variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5" onClick={() => navigate("/dashboard")}>Back to App</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap text-sm font-medium transition-all ${activeTab === tab.id ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "bg-slate-800/50 text-slate-400 border border-white/5 hover:bg-slate-800"}`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-violet-400 animate-spin" /></div>
        ) : (
          <>
            {activeTab === "dashboard" && <AdminDashboard dash={data.dashboard || {}} />}
            {activeTab === "analytics" && <AdminAnalytics ana={data.analytics || {}} period={period} setPeriod={setPeriod} />}
            {activeTab === "usage" && <AdminUsage ana={data.analytics || {}} period={period} setPeriod={setPeriod} />}
            {activeTab === "ai-usage" && <AdminAIUsage />}
            {activeTab === "costs" && <AdminCosts costs={data.costs || {}} />}
            {activeTab === "users" && (
              <AdminUsers
                users={users} usersTotal={usersTotal}
                searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                loadUsers={loadUsers} handleExportCSV={handleExportCSV}
                selectedUser={selectedUser} setSelectedUser={setSelectedUser}
                showUserDialog={showUserDialog} setShowUserDialog={setShowUserDialog}
                handleUpdateRole={handleUpdateRole} handleToggleSub={handleToggleSub}
                handleExtendTrial={handleExtendTrial} handleDeleteUser={handleDeleteUser}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
